// Данные «Входящих» (032: T030, T031; FR-001, FR-003; ASG-HIST-03, ASG-HIST-04; plan.md, «Решения»):
// условия экрана в адресе, запрос ленты и правка кэша при прочтении.
//
// Адрес: /inbox?n=<номер уведомления> — выбранная строка (ссылка и «Назад» браузера работают),
// tab — вкладка («Все» — без параметра), unread — «Непрочитанные».
//
// Лента — тот же маршрут notifications.list, что у колокольчика (031), с вкладкой: ключ кэша всегда
// с tab, поэтому он не совпадает с фоновым опросом счётчиков (notificationsQuery). Прочтение —
// оптимистично во всех кэшах ленты: строка становится прочитанной, счётчики вкладок, меню
// и колокольчика уменьшаются сразу; сервер ответил — перечитываются все ленты, кроме открытой:
// прочитанная строка остаётся на месте и при «Непрочитанных», пока ленту не перечитают.
//
// 0.26.1 (ACC-06): open — «Нерешённые»: строки «требует действия» без отметки разбора, прочитанные
// тоже (запрос open=true). «Отметить разобранным» — оптимистично во всех кэшах ленты (resolveOne):
// строка — с отметкой «закрыто» и прочитана, число нерешённых (openCount) — на одно меньше.
// Ревью 0.26.1 (W3): число меню и колокольчика (actionUnread) — непрочитанные нерешённые строки
// «требует действия»: прочтение разобранной строки его не меняет. «Отменить» разбор
// (notifications.unresolve) — без правки кэша: ленты и карточка перечитываются с сервера (W1).
import { inboxTabs } from '@kp/contracts';
import type { InboxTab, Notification, NotificationsResponse } from '@kp/contracts';
import { apiKey, apiQuery } from '../../api/client.ts';

export interface InboxSearch {
  /** Выбранное уведомление: его карточка справа, до 900 px — слоем поверх списка. */
  n?: string | undefined;
  /** Вкладка; «Все» — без параметра. */
  tab?: Exclude<InboxTab, 'all'> | undefined;
  /** «Непрочитанные». */
  unread?: true | undefined;
  /** 0.26.1 (ACC-06): «Нерешённые» — «требует действия» без отметки разбора. */
  open?: true | undefined;
}

/** Чужие значения отбрасываются: корневой маршрут пропускает параметры адреса как есть. */
export function parseInboxSearch(search: Record<string, unknown>): InboxSearch {
  const n = typeof search.n === 'string' && search.n !== '' ? search.n : undefined;
  const tab = inboxTabs.find(
    (value): value is Exclude<InboxTab, 'all'> => value !== 'all' && value === search.tab,
  );
  const on = (value: unknown) =>
    value === true || value === 'true' || value === 1 ? true : undefined;
  return { n, tab, unread: on(search.unread), open: on(search.open) };
}

/** Все ленты уведомлений в кэше: колокольчик, счётчики меню, вкладки «Входящих». */
export const INBOX_FEED_KEY = ['api', 'notifications.list'] as const;

/** Условия ленты: вкладка, «Непрочитанные», «Нерешённые». */
export interface InboxListFilter {
  readonly tab: InboxTab;
  readonly unread: boolean;
  readonly open: boolean;
}

function listInput({ tab, unread, open }: InboxListFilter) {
  return {
    query: { tab, ...(unread ? { unread: 'true' } : {}), ...(open ? { open: 'true' } : {}) },
  };
}

/** Ключ ленты вкладки: с tab — не совпадает с ключом фонового опроса счётчиков. */
export function inboxListKey(filter: InboxListFilter): readonly unknown[] {
  return apiKey('notifications.list', listInput(filter));
}

/** Лента вкладки перечитывается при каждом открытии экрана. */
export function inboxListQuery(filter: InboxListFilter) {
  return { ...apiQuery('notifications.list', listInput(filter)), staleTime: 0 };
}

const less = (count: number) => Math.max(0, count - 1);

/**
 * Одно уведомление прочитано: строка — с отметкой, счётчики — на одно меньше (непрочитанные,
 * «Все» и вкладка строки; «требует действия» — только у нерешённой строки: разобранную сервер
 * в этом числе не считает, ревью 0.26.1 W3). Вызывается только для непрочитанной строки.
 */
export function readOne(
  data: NotificationsResponse,
  item: Notification,
  at: string,
): NotificationsResponse {
  const tabUnread = { ...data.tabUnread, all: less(data.tabUnread.all) };
  if (item.tab) tabUnread[item.tab] = less(data.tabUnread[item.tab]);
  return {
    ...data,
    items: data.items.map((row) =>
      row.id === item.id ? { ...row, readAt: row.readAt ?? at } : row,
    ),
    unreadCount: less(data.unreadCount),
    actionUnread: isOpenTask(item) ? less(data.actionUnread) : data.actionUnread,
    tabUnread,
  };
}

/** «Прочитать все»: все строки — с отметкой, счётчики — ноль. */
export function readEvery(data: NotificationsResponse, at: string): NotificationsResponse {
  return {
    ...data,
    items: data.items.map((row) => (row.readAt ? row : { ...row, readAt: at })),
    unreadCount: 0,
    actionUnread: 0,
    tabUnread: { all: 0, assigned: 0, due: 0, changes: 0 },
  };
}

/** Строка «требует действия», которую ещё не разобрали. */
export function isOpenTask(item: Notification): boolean {
  return item.actionRequired === true && item.resolvedAt === undefined;
}

/**
 * 0.26.1 (ACC-06): «Отметить разобранным» — строка закрыта и прочитана (маршрут notifications.resolve
 * отмечает и прочтение), нерешённых — на одну меньше. Уже разобранная — без изменений.
 */
export function resolveOne(
  data: NotificationsResponse,
  item: Notification,
  at: string,
): NotificationsResponse {
  if (!isOpenTask(item)) return data;
  const read = item.readAt ? data : readOne(data, item, at);
  return {
    ...read,
    items: read.items.map((row) =>
      row.id === item.id && row.resolvedAt === undefined
        ? { ...row, readAt: row.readAt ?? at, resolvedAt: at, resolution: 'closed' }
        : row,
    ),
    openCount: less(read.openCount),
  };
}
